import { signal, WritableSignal } from '@angular/core';
import { TASK_STATUS_TRANSLATION_KEYS } from '@app/shared/types/enums/task-status.enum';
import { Task } from '@app/shared/types/models/task/task.model';
import { PROFFEO_TASK_PRIORITY_TOKENS } from '@app/shared/ui/tokens/task-priority.tokens';
import { buildProffeoUiTableGroups } from '@app/shared/ui/types/table.types.util';

export type TaskGrouping = 'none' | 'status' | 'priority' | 'industry' | 'location';
export type TaskGroupingField = Exclude<TaskGrouping, 'none'>;
export const TASK_GROUPING_FIELDS: readonly TaskGroupingField[] = ['status', 'priority', 'industry', 'location'];
/** Saved grouping (task table `groupByColumnId`) to a criterion; unknown values fall back to none. */
export function parseTaskGrouping(value: unknown): TaskGrouping {
  return TASK_GROUPING_FIELDS.find(field => field === value) ?? 'none';
}
export interface TaskPageGroup {
  id: string;
  label: string;
  translateLabel: boolean;
  tasks: Task[];
}

/** Shared domain identity. Names are labels, never identifiers. No API or cache ownership. */
export function taskGroupingValue(task: Task, grouping: TaskGroupingField): string | null {
  switch (grouping) {
    case 'status':
      return task.status ?? null;
    case 'priority':
      return task.priority ?? null;
    case 'industry':
      return task.projectIndustry?.()?.id ?? null;
    case 'location':
      return task.location?.()?.id ?? null;
  }
}
export function tasksShareGroup(first: Task, second: Task, grouping: TaskGroupingField): boolean {
  return taskGroupingValue(first, grouping) === taskGroupingValue(second, grouping);
}
export function taskGroupLabel(task: Task, grouping: TaskGroupingField): { label: string; translateLabel: boolean } {
  let label: string | undefined;
  if (grouping === 'status') label = TASK_STATUS_TRANSLATION_KEYS.get(task.status);
  if (grouping === 'priority') label = PROFFEO_TASK_PRIORITY_TOKENS[task.priority]?.translationKey;
  if (grouping === 'industry') label = task.projectIndustry?.()?.industry?.()?.name?.trim();
  if (grouping === 'location') label = task.location?.()?.name?.trim();
  return {
    label: label || 'task-list-grouping.unassigned',
    translateLabel: grouping === 'status' || grouping === 'priority' || !label
  };
}
/** Root membership stays stable across model replacements, sorting and child loading. */
export function taskGroupId(grouping: TaskGroupingField, tasks: readonly Task[]): string {
  return `${grouping}:${JSON.stringify(tasks.map(task => task.id ?? '').sort())}`;
}
/** PROPOZYCJA — DO WERYFIKACJI: group supplied page roots, preserving their order. */
export function groupTaskPage(tasks: readonly Task[], grouping: TaskGrouping): TaskPageGroup[] {
  if (grouping === 'none') return [];
  return buildProffeoUiTableGroups(
    tasks,
    grouping,
    (a, b) => tasksShareGroup(a, b, grouping),
    task => taskGroupLabel(task, grouping).label,
    task => task.id ?? ''
  ).map(group => ({
    id: taskGroupId(grouping, group.rows),
    ...taskGroupLabel(group.representative, grouping),
    tasks: group.rows
  }));
}
export function taskGroupSignature(groups: readonly Pick<TaskPageGroup, 'id'>[]): string {
  return JSON.stringify(groups.map(group => group.id).sort());
}
/** Route-owned presentation state, embedded in existing TaskFiltersService; not a second data store. */
export interface TaskGroupExpansionState {
  expandedTaskGroupIds: WritableSignal<Set<string>>;
  reconcileTaskGroups: (signature: string) => void;
}
export function createTaskGroupExpansionState(): TaskGroupExpansionState {
  const expandedTaskGroupIds = signal<Set<string>>(new Set());
  let previousSignature: string | undefined;
  const reconcileTaskGroups = (signature: string): void => {
    if (signature === previousSignature) return;
    previousSignature = signature;
    expandedTaskGroupIds.set(new Set());
  };
  return { expandedTaskGroupIds, reconcileTaskGroups };
}
